import React from 'react';
import { motion } from 'framer-motion';
import { ArrowUpRight, Shield } from 'lucide-react';
import Navigation from '../components/Navigation';
import { Footer } from '../components/legal';

/**
 * Rechner-Seite (umbenannt zu "Tools & Planora")
 * Verweist auf Planora für Tools, keine eingebetteten Rechner
 */

const Rechner = () => {
  return (
    <div className="min-h-screen bg-navy">
      <Navigation isDark={true} />

      {/* Hero Section */}
      <section className="pt-32 sm:pt-40 md:pt-48 pb-16 sm:pb-24 md:pb-32 relative overflow-hidden">
        {/* Background Elements */}
        <div className="absolute inset-0">
          <div className="absolute top-1/4 right-0 w-[500px] h-[500px] bg-cobalt/5 rounded-full blur-[150px] translate-x-1/3" />
          <div className="absolute bottom-0 left-1/4 w-[300px] h-[300px] bg-slate/5 rounded-full blur-[100px]" />
        </div>

        <div className="max-w-6xl mx-auto px-6 md:px-8 relative">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            
            {/* Left: Text Content */}
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7 }}
              className="order-2 lg:order-1"
            >
              {/* Label */}
              <p className="text-white/30 text-xs font-medium uppercase tracking-[0.25em] mb-6">
                Die Idee dahinter
              </p>

              {/* Main Headline */}
              <h1 className="font-playfair text-3xl sm:text-4xl md:text-[2.75rem] lg:text-5xl font-bold text-white leading-[1.15] tracking-tight mb-6">
                Finanzielle Entscheidungen
                <span className="block mt-1 text-white/40">fürs ganze Leben.</span>
              </h1>

              {/* Subheadline */}
              <p className="text-base sm:text-lg text-white/50 leading-relaxed mb-10 max-w-lg">
                In der Schule lernen wir es nicht. Im Beruf zeigt es uns niemand. 
                Und trotzdem wird von uns erwartet, dass wir unsere Absicherung selbst in die Hand nehmen.
              </p>

              {/* Personal Text */}
              <div className="space-y-4 mb-10">
                <p className="text-white/70 text-base leading-relaxed">
                  Was passiert, wenn du nicht mehr arbeiten kannst? 
                  Wie groß ist die Lücke wirklich? Das erklärt dir keiner.
                </p>
                <p className="text-white/50 text-base leading-relaxed">
                  Genau dieser Widerspruch hat mich nicht mehr losgelassen. 
                  Aus diesem Gedanken heraus entsteht Schritt für Schritt eine Plattform 
                  mit Tools und verständlichen Inhalten, die Absicherungsthemen 
                  einfacher und greifbarer machen soll.
                </p>
              </div>
            </motion.div>

            {/* Right: Portrait Image */}
            <motion.div
              initial={{ opacity: 0, scale: 0.98 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="order-1 lg:order-2 flex justify-center lg:justify-end"
            >
              <div className="relative w-full max-w-md lg:max-w-none">
                {/* Glow behind */}
                <div className="absolute inset-0 bg-gradient-to-br from-cobalt/20 via-slate/10 to-transparent rounded-3xl blur-2xl scale-105" />
                
                {/* Main Image Container */}
                <div className="relative aspect-[4/5] rounded-3xl overflow-hidden border border-white/[0.08]">
                  <img 
                    src="/images/nico-rechner.jpg"
                    alt="Nico Helfrich"
                    className="w-full h-full object-cover object-top"
                  />
                  
                  {/* Overlay Gradient */}
                  <div className="absolute bottom-0 inset-x-0 h-32 bg-gradient-to-t from-navy/60 to-transparent" />
                  
                  {/* Corner Accents */}
                  <div className="absolute top-6 left-6 w-12 h-12 border-l-2 border-t-2 border-white/20 rounded-tl-lg" />
                  <div className="absolute bottom-6 right-6 w-12 h-12 border-r-2 border-b-2 border-white/20 rounded-br-lg" />
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Divider */}
      <div className="max-w-4xl mx-auto px-6 md:px-8">
        <div className="h-px bg-gradient-to-r from-transparent via-white/10 to-transparent" />
      </div>

      {/* Planora Section */}
      <section className="py-16 sm:py-20 relative">
        <div className="max-w-2xl mx-auto px-6 md:px-8">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            {/* Premium Planora Card */}
            <motion.div 
              className="relative group"
              whileHover={{ y: -4 }}
              transition={{ type: "spring", stiffness: 400, damping: 25 }}
            >
              {/* Outer Glow Layer */}
              <div className="absolute -inset-1 bg-gradient-to-r from-cobalt/20 via-blue-500/30 to-indigo-500/20 rounded-2xl blur-xl opacity-60 group-hover:opacity-100 transition-opacity duration-500" />
              
              {/* Main Card */}
              <div className="relative bg-gradient-to-b from-[#1a2035] to-[#0d1117] rounded-xl overflow-hidden border border-white/[0.08] shadow-2xl shadow-black/50">
                {/* Inner Light Edge */}
                <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/20 to-transparent" />
                
                {/* Content Container */}
                <div className="relative p-6 sm:p-8 flex flex-col sm:flex-row sm:items-center gap-6 sm:gap-8">
                  
                  {/* Logo Container */}
                  <div className="relative flex-shrink-0">
                    <div className="absolute -inset-4 bg-cobalt/20 rounded-2xl blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                    <div className="relative w-36 h-36 sm:w-44 sm:h-44">
                      <img 
                        src="/images/planora-logo-premium.png" 
                        alt="Planora Finance" 
                        className="w-full h-full object-contain drop-shadow-2xl rounded-xl"
                      />
                    </div>
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <h2 className="font-playfair text-xl sm:text-2xl font-bold text-white leading-tight mb-2">
                      Aus diesen Ideen entsteht etwas Größeres.
                    </h2>
                    <p className="text-white/40 text-sm sm:text-base leading-relaxed mb-5">
                      Eine moderne Plattform für finanzielle Lebensplanung.
                    </p>

                    {/* CTAs */}
                    <div className="flex flex-wrap gap-3">
                      <a
                        href="https://planora.finance/tools"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="group/btn relative inline-flex items-center gap-2"
                      >
                        <div className="absolute -inset-1 bg-white/10 rounded-full blur-md opacity-0 group-hover/btn:opacity-100 transition-opacity" />
                        <div className="relative flex items-center gap-2 px-4 py-2 bg-gradient-to-b from-white to-gray-100 text-navy rounded-full text-sm font-medium shadow-lg shadow-black/20 group-hover/btn:shadow-xl transition-all duration-300">
                          <span>Zu den Tools</span>
                          <ArrowUpRight className="w-3.5 h-3.5 group-hover/btn:translate-x-0.5 group-hover/btn:-translate-y-0.5 transition-transform" />
                        </div>
                      </a>

                      <a
                        href="https://planora.finance/insights"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="group/btn relative inline-flex items-center gap-2"
                      >
                        <div className="relative flex items-center gap-2 px-4 py-2 bg-white/5 hover:bg-white/10 text-white/80 hover:text-white border border-white/10 rounded-full text-sm font-medium transition-all duration-300">
                          <span>Perspektiven</span>
                          <ArrowUpRight className="w-3.5 h-3.5 group-hover/btn:translate-x-0.5 group-hover/btn:-translate-y-0.5 transition-transform" />
                        </div>
                      </a>
                    </div>
                  </div>
                </div>
                
                {/* Bottom Reflection */}
                <div className="absolute inset-x-0 bottom-0 h-12 bg-gradient-to-t from-cobalt/[0.03] to-transparent pointer-events-none" />
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Free for Clients Note */}
      <section className="py-10 sm:py-12 relative">
        <div className="max-w-2xl mx-auto px-6 md:px-8">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <p className="text-white/30 text-sm leading-relaxed">
              Die vollständige Software ist für meine Kunden kostenfrei nutzbar
              <span className="mx-2 text-white/15">—</span>
              <span className="text-white/40">während vergleichbare Lösungen normalerweise <span className="whitespace-nowrap">5–15 €</span>/Monat kosten.</span>
            </p>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 sm:py-20 relative">
        <div className="max-w-3xl mx-auto px-6 md:px-8 text-center">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-cobalt/10 border border-cobalt/20 rounded-full mb-6">
              <Shield className="w-3.5 h-3.5 text-cobalt" />
              <span className="text-xs text-cobalt font-medium">Persönliche Beratung</span>
            </div>
            <h2 className="font-playfair text-2xl sm:text-3xl font-bold text-white mb-4">
              Lass uns deine Situation besprechen
            </h2>
            <p className="text-white/50 text-base mb-8 max-w-lg mx-auto">
              Im kostenlosen Erstgespräch schauen wir uns an, wo du stehst und 
              welche Absicherungslücken es gibt.
            </p>
            <a
              href="/strategiegespraech"
              className="inline-flex items-center gap-2 px-6 py-3 bg-cobalt text-white rounded-xl font-medium hover:bg-cobalt/90 transition-all shadow-lg shadow-cobalt/30"
            >
              Gespräch vereinbaren
              <ArrowUpRight className="w-4 h-4" />
            </a>
          </motion.div>
        </div>
      </section>

      <Footer variant="dark" />
    </div>
  );
};

export default Rechner;
